import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Snackbar } from "@mui/material";
import SocieteAndUserService from "../../../services/societeAndUser";
import uploadAndDownloadService from "../../../services/uploadAndDownloadFile";
import ClientContractService from "../../../services/ClientContractService";
import SocieteInformation from "../../../ui/SocieteInformation";
import SocieteDefaultsDialog from "../../../ui/SocieteDefaultsDialog";

const GREEN = "#28a745";
const PAGE_SIZES = [10, 25, 50];

// the clients are listed by their subscription: "AVEC" (active contract), "EXPIRE" or "SANS"
const ABONNEMENTS = {
  AVEC: { label: "Avec abonnement", badge: "badge-success", icon: "fa-check-circle", color: GREEN },
  EXPIRE: { label: "Expiré", badge: "badge-danger", icon: "fa-exclamation-circle", color: "#dc3545" },
  SANS: { label: "Sans abonnement", badge: "badge-secondary", icon: "fa-minus-circle", color: "#6c757d" },
};

// Bootstrap's focus rings are blue: the page is green (the colour of the brand)
const GreenStyle = () => (
  <style>{`
    .noryx-green .form-control:focus {
      border-color: ${GREEN};
      box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
    }
    .noryx-green .kpi { cursor: pointer; transition: box-shadow .15s, transform .15s; border: 0; text-align: left; width: 100%; }
    .noryx-green .kpi:hover { box-shadow: 0 4px 14px rgba(0,0,0,.12) !important; transform: translateY(-1px); }
    .noryx-green .kpi.selected { box-shadow: 0 0 0 2px ${GREEN} !important; }
    .noryx-green .page-link { color: ${GREEN}; }
    .noryx-green .page-item.active .page-link { background-color: ${GREEN}; border-color: ${GREEN}; color: #fff; }
  `}</style>
);

const formatIso = (value) => {
  if (!value) return "";
  const [y, m, d] = String(value).slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : "";
};

// whole days from today to a yyyy-MM-dd date (negative when past)
const daysUntil = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((new Date(y, m - 1, d) - today) / 86400000);
};

const initialsOf = (title) =>
  (title || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");

// logo of a company, or its initials on a green disc when it has none (or it cannot be loaded)
const Logo = ({ title, src, size = 42 }) =>
  src ? (
    <img
      src={src}
      alt={`Logo de ${title}`}
      className="rounded-circle border bg-white"
      style={{ width: size, height: size, objectFit: "cover" }}
    />
  ) : (
    <span
      className="rounded-circle d-inline-flex align-items-center justify-content-center text-white font-weight-bold"
      style={{ width: size, height: size, backgroundColor: GREEN, fontSize: size * 0.38 }}
      aria-hidden="true"
    >
      {initialsOf(title) || <i className="fas fa-building"></i>}
    </span>
  );

const Kpi = ({ icon, color, value, label, selected, onClick }) => (
  <div className="col-6 col-lg">
    <button type="button" className={`kpi card shadow-sm mb-3 p-3${selected ? " selected" : ""}`} onClick={onClick}>
      <div className="d-flex align-items-center">
        <span
          className="rounded-circle d-inline-flex align-items-center justify-content-center text-white mr-3"
          style={{ width: 42, height: 42, backgroundColor: color }}
        >
          <i className={`fas ${icon}`}></i>
        </span>
        <div>
          <div className="h4 mb-0">{value}</div>
          <div className="text-muted small">{label}</div>
        </div>
      </div>
    </button>
  </div>
);

const AdminSocietes = () => {
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [archived, setArchived] = useState([]);
  const [loadState, setLoadState] = useState("loading"); // loading | ready | error
  const [contractMap, setContractMap] = useState({});
  const [logos, setLogos] = useState({});
  const [informationModel, setInformationModel] = useState();
  const [onShowInformationModel, setOnShowInformationModel] = useState(false);
  const [alert, setAlert] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [defaultsFor, setDefaultsFor] = useState(null); // the company whose defaults are being completed

  const [search, setSearch] = useState("");
  const [abonnement, setAbonnement] = useState("TOUS"); // TOUS | AVEC | EXPIRE | SANS
  const [status, setStatus] = useState("ACTIFS"); // ACTIFS | ARCHIVEES | TOUS
  const [sort, setSort] = useState("RECENT"); // RECENT | NOM
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const loadedLogos = useRef({}); // id -> object url (revoked when the page closes)

  const load = useCallback(() => {
    setLoadState("loading");
    // the first argument is the type of the partner, the second one 0 (active) or 1 (archived)
    Promise.all([
      SocieteAndUserService.getAllPartenaires("CLIENT", 0),
      SocieteAndUserService.getAllPartenaires("CLIENT", 1),
    ])
      .then(([active, deleted]) => {
        const list = (response) =>
          response.data && Array.isArray(response.data.ListSociete) ? response.data.ListSociete : null;
        const activeList = list(active);
        const deletedList = list(deleted);
        if (!activeList || !deletedList) {
          setLoadState("error");
          return;
        }
        setClients(activeList);
        setArchived(deletedList);
        setLoadState("ready");
      })
      .catch(() => setLoadState("error"));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    ClientContractService.getSummary()
      .then((response) => {
        const map = {};
        ((response.data && response.data.success) || []).forEach((contract) => {
          map[contract.idSociete] = contract;
        });
        setContractMap(map);
      })
      .catch(() => {});
  }, []);

  // logos: loaded once per company, an unreadable one keeps the initials
  useEffect(() => {
    [...clients, ...archived].forEach((row) => {
      if (!row.fileUrl || loadedLogos.current[row.id] !== undefined) return;
      loadedLogos.current[row.id] = null;
      uploadAndDownloadService
        .downloadFile(row.fileUrl)
        .then((response) => {
          const url = URL.createObjectURL(new Blob([response.data]));
          loadedLogos.current[row.id] = url;
          setLogos((old) => ({ ...old, [row.id]: url }));
        })
        .catch(() => {});
    });
  }, [clients, archived]);

  useEffect(
    () => () => {
      Object.values(loadedLogos.current).forEach((url) => url && URL.revokeObjectURL(url));
    },
    []
  );

  const abonnementOf = (id) => {
    const current = contractMap[id] && contractMap[id].status;
    return current === "ACTIF" ? "AVEC" : current === "EXPIRE" ? "EXPIRE" : "SANS";
  };

  const counts = useMemo(() => {
    const result = { AVEC: 0, EXPIRE: 0, SANS: 0 };
    clients.forEach((row) => {
      const key = contractMap[row.id] && contractMap[row.id].status;
      result[key === "ACTIF" ? "AVEC" : key === "EXPIRE" ? "EXPIRE" : "SANS"] += 1;
    });
    return result;
  }, [clients, contractMap]);

  const rows = useMemo(() => {
    const base = status === "ACTIFS" ? clients : status === "ARCHIVEES" ? archived : [...clients, ...archived];
    const needle = search.trim().toLowerCase();
    const filtered = base.filter((row) => {
      if (abonnement !== "TOUS" && abonnementOf(row.id) !== abonnement) return false;
      if (!needle) return true;
      return [row.title, row.matricule, row.email, row.numPhone].some(
        (value) => value && String(value).toLowerCase().includes(needle)
      );
    });
    const sorted = [...filtered];
    if (sort === "NOM") {
      sorted.sort((a, b) => String(a.title || "").localeCompare(String(b.title || ""), "fr", { sensitivity: "base" }));
    } else {
      sorted.sort((a, b) => String(b.creationDate || "").localeCompare(String(a.creationDate || "")));
    }
    return sorted;
  }, [clients, archived, status, search, abonnement, sort, contractMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageRows = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const choose = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const onShowInformation = (row) => {
    setInformationModel({
      matricule: row.matricule,
      adress: row.adresse,
      creationDate: row.creationDate ? String(row.creationDate).replaceAll("-", "/") : "",
      nbrEtbl: row.numberEtabSecondaire,
      codeTva: row.codeTVA,
      image: logos[row.id],
      typeSociete: row.typeSociete,
    });
    setOnShowInformationModel(true);
  };

  const onArchiveOrRestore = (row) => {
    const archiving = row.isDeleted === 0 || row.isDeleted === "0";
    const question = archiving
      ? `Archiver « ${row.title} » ? La société ne sera plus disponible.`
      : `Restaurer « ${row.title} » ?`;
    if (!window.confirm(question)) return;
    setBusyId(row.id);
    SocieteAndUserService.deleteAndRestaureSociete(row.id, row.isDeleted)
      .then((response) => {
        if (response.data && Object.keys(response.data)[0] === "success") {
          setAlert({ severity: "success", text: archiving ? "Société archivée" : "Société restaurée" });
          load();
        } else {
          setAlert({ severity: "error", text: "L'opération a échoué" });
        }
      })
      .catch(() => setAlert({ severity: "error", text: "Erreur : problème de connexion" }))
      .finally(() => setBusyId(null));
  };

  const subscriptionCell = (row) => {
    const key = abonnementOf(row.id);
    const info = ABONNEMENTS[key];
    const contract = contractMap[row.id];
    const remaining = key === "AVEC" ? daysUntil(contract.endDate) : null;
    return (
      <>
        <span className={`badge ${info.badge}`}>{info.label}</span>
        {contract && contract.endDate && (
          <div className="small text-muted mt-1">
            {key === "EXPIRE" ? "Expiré le " : "Fin le "}
            {formatIso(contract.endDate)}
            {remaining !== null && remaining >= 0 && remaining <= 30 && (
              <span className="text-warning font-weight-bold"> · {remaining} j</span>
            )}
          </div>
        )}
      </>
    );
  };

  const filtersActive = search.trim() !== "" || abonnement !== "TOUS" || status !== "ACTIFS";

  return (
    <div className="content-wrapper noryx-green" style={{ marginTop: "0" }}>
      <GreenStyle />
      <section className="content-header">
        <div className="container-fluid">
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                <i className="fas fa-building mr-2 text-success"></i>Les sociétés clientes
              </h4>
              <small className="text-muted">
                Gérez les sociétés clientes, leurs abonnements et leurs contrats
              </small>
            </div>
            <button type="button" className="btn btn-success mt-2 mt-sm-0" onClick={() => navigate("/addAdminSociete")}>
              <i className="fas fa-plus mr-2"></i>Ajouter une société
            </button>
          </div>
        </div>
      </section>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <Kpi
              icon="fa-building"
              color={GREEN}
              value={clients.length}
              label="Sociétés actives"
              selected={abonnement === "TOUS" && status === "ACTIFS"}
              onClick={() => {
                setAbonnement("TOUS");
                setStatus("ACTIFS");
                setPage(1);
              }}
            />
            {Object.keys(ABONNEMENTS).map((key) => (
              <Kpi
                key={key}
                icon={ABONNEMENTS[key].icon}
                color={ABONNEMENTS[key].color}
                value={counts[key]}
                label={ABONNEMENTS[key].label}
                selected={abonnement === key && status === "ACTIFS"}
                onClick={() => {
                  setAbonnement(key);
                  setStatus("ACTIFS");
                  setPage(1);
                }}
              />
            ))}
            <Kpi
              icon="fa-archive"
              color="#6c757d"
              value={archived.length}
              label="Archivées"
              selected={status === "ARCHIVEES"}
              onClick={() => {
                setAbonnement("TOUS");
                setStatus("ARCHIVEES");
                setPage(1);
              }}
            />
          </div>

          <div className="card shadow-sm" style={{ borderRadius: 8 }}>
            <div className="card-header">
              <div className="form-row align-items-center">
                <div className="col-12 col-md-5 mb-2 mb-md-0">
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text bg-white">
                        <i className="fas fa-search text-muted"></i>
                      </span>
                    </div>
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Rechercher par raison sociale, matricule, email ou téléphone"
                      aria-label="Rechercher une société"
                      value={search}
                      onChange={(e) => choose(setSearch)(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <select
                    className="form-control"
                    aria-label="Filtrer par abonnement"
                    value={abonnement}
                    onChange={(e) => choose(setAbonnement)(e.target.value)}
                  >
                    <option value="TOUS">Tous les abonnements</option>
                    <option value="AVEC">Avec abonnement</option>
                    <option value="SANS">Sans abonnement</option>
                    <option value="EXPIRE">Abonnement expiré</option>
                  </select>
                </div>
                <div className="col-6 col-md-2">
                  <select
                    className="form-control"
                    aria-label="Filtrer par état"
                    value={status}
                    onChange={(e) => choose(setStatus)(e.target.value)}
                  >
                    <option value="ACTIFS">Actives</option>
                    <option value="ARCHIVEES">Archivées</option>
                    <option value="TOUS">Toutes</option>
                  </select>
                </div>
                <div className="col-12 col-md-2 mt-2 mt-md-0">
                  <select
                    className="form-control"
                    aria-label="Trier"
                    value={sort}
                    onChange={(e) => choose(setSort)(e.target.value)}
                  >
                    <option value="RECENT">Plus récentes</option>
                    <option value="NOM">Nom A → Z</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="card-body p-0">
              {loadState === "loading" && (
                <div className="text-center py-5">
                  <div className="spinner-border text-success" role="status">
                    <span className="sr-only">Chargement...</span>
                  </div>
                  <div className="text-muted mt-2">Chargement des sociétés...</div>
                </div>
              )}

              {loadState === "error" && (
                <div className="alert alert-danger m-3 d-flex justify-content-between align-items-center">
                  <span>Impossible de charger les sociétés (problème de connexion).</span>
                  <button className="btn btn-sm btn-outline-danger" onClick={load}>
                    <i className="fas fa-redo mr-1"></i>Réessayer
                  </button>
                </div>
              )}

              {loadState === "ready" && rows.length === 0 && (
                <div className="text-center text-muted py-5">
                  <i className="fas fa-building fa-2x mb-2 d-block"></i>
                  {filtersActive ? "Aucune société ne correspond à votre recherche." : "Aucune société cliente pour le moment."}
                  {filtersActive ? (
                    <div className="mt-2">
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() => {
                          setSearch("");
                          setAbonnement("TOUS");
                          setStatus("ACTIFS");
                          setPage(1);
                        }}
                      >
                        Effacer les filtres
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <button className="btn btn-sm btn-success" onClick={() => navigate("/addAdminSociete")}>
                        <i className="fas fa-plus mr-1"></i>Ajouter une société
                      </button>
                    </div>
                  )}
                </div>
              )}

              {loadState === "ready" && rows.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th>Société</th>
                        <th>Contact</th>
                        <th>Fiscalité</th>
                        <th>Abonnement</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.map((row) => {
                        const isArchived = !(row.isDeleted === 0 || row.isDeleted === "0");
                        return (
                          <tr key={row.id} className={isArchived ? "text-muted" : undefined}>
                            <td>
                              <div className="d-flex align-items-center">
                                <Logo title={row.title} src={logos[row.id]} />
                                <div className="ml-3">
                                  <div className="font-weight-bold">
                                    {row.title}
                                    {isArchived && <span className="badge badge-secondary ml-2">Archivée</span>}
                                  </div>
                                  <div className="small text-muted">
                                    {row.matricule || "—"}
                                    {row.codeCategorie && (
                                      <span className="badge badge-light border ml-2">Catégorie {row.codeCategorie}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              {row.email ? <a href={`mailto:${row.email}`}>{row.email}</a> : <span className="text-muted">—</span>}
                              <div className="small">
                                {row.numPhone ? (
                                  <a href={`tel:${String(row.numPhone).replace(/\s/g, "")}`}>{row.numPhone}</a>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </div>
                            </td>
                            <td>
                              {row.typeTva === "Normale" || !row.typeTva ? (
                                <span>TVA {row.tva !== undefined && row.tva !== null ? `${row.tva} %` : "—"}</span>
                              ) : (
                                <span className="badge badge-warning text-white">{row.typeTva}</span>
                              )}
                              <div className="small text-muted">
                                Timbre {row.timbre !== undefined && row.timbre !== null ? row.timbre : "—"}
                              </div>
                            </td>
                            <td>{subscriptionCell(row)}</td>
                            <td className="text-right text-nowrap">
                              <button
                                type="button"
                                className="btn btn-outline-success btn-sm mr-1"
                                onClick={() => navigate(`/contratAbonnement?idSociete=${row.id}`)}
                                title="Contrat d'abonnement"
                              >
                                <i className="fas fa-file-contract mr-1"></i>Contrat
                              </button>
                              {!isArchived && (
                                <button
                                  type="button"
                                  className="btn btn-outline-success btn-sm mr-1"
                                  onClick={() => setDefaultsFor(row)}
                                  title="Dépôt central et catalogue par défaut"
                                  aria-label={`Configuration par défaut de ${row.title}`}
                                >
                                  <i className="fas fa-cogs"></i>
                                </button>
                              )}
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm mr-1"
                                onClick={() => onShowInformation(row)}
                                title="Voir les informations"
                                aria-label={`Informations de ${row.title}`}
                              >
                                <i className="fas fa-info-circle"></i>
                              </button>
                              <button
                                type="button"
                                className={`btn btn-sm ${isArchived ? "btn-outline-success" : "btn-outline-danger"}`}
                                disabled={busyId === row.id}
                                onClick={() => onArchiveOrRestore(row)}
                                title={isArchived ? "Restaurer la société" : "Archiver la société"}
                                aria-label={`${isArchived ? "Restaurer" : "Archiver"} ${row.title}`}
                              >
                                {busyId === row.id ? (
                                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : (
                                  <i className={`fas ${isArchived ? "fa-undo" : "fa-archive"}`}></i>
                                )}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {loadState === "ready" && rows.length > 0 && (
              <div className="card-footer d-flex flex-wrap justify-content-between align-items-center">
                <div className="text-muted small">
                  {rows.length} société{rows.length > 1 ? "s" : ""} · page {currentPage} / {pageCount}
                </div>
                <div className="d-flex align-items-center">
                  <select
                    className="form-control form-control-sm mr-3"
                    style={{ width: "auto" }}
                    aria-label="Nombre de sociétés par page"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {PAGE_SIZES.map((size) => (
                      <option key={size} value={size}>
                        {size} par page
                      </option>
                    ))}
                  </select>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item${currentPage === 1 ? " disabled" : ""}`}>
                      <button className="page-link" onClick={() => setPage(currentPage - 1)} aria-label="Page précédente">
                        <i className="fas fa-chevron-left"></i>
                      </button>
                    </li>
                    <li className="page-item active">
                      <span className="page-link">{currentPage}</span>
                    </li>
                    <li className={`page-item${currentPage === pageCount ? " disabled" : ""}`}>
                      <button className="page-link" onClick={() => setPage(currentPage + 1)} aria-label="Page suivante">
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {onShowInformationModel && (
        <SocieteInformation
          setOnShowInformationModel={setOnShowInformationModel}
          informationModel={informationModel}
        />
      )}

      {defaultsFor && (
        <SocieteDefaultsDialog
          societe={defaultsFor}
          onClose={() => setDefaultsFor(null)}
          onDone={() => setAlert({ severity: "success", text: "Configuration par défaut créée pour " + defaultsFor.title })}
        />
      )}

      {alert && (
        <Snackbar
          open
          autoHideDuration={3500}
          onClose={() => setAlert(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert variant="filled" onClose={() => setAlert(null)} severity={alert.severity}>
            {alert.text}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
};

export default AdminSocietes;
